import { useCallback, useEffect } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  ArrowSquareOutIcon,
  CaretLeftIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';
import { isEmailBodyDocument } from '@erp/shared-utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DocumentViewer } from '@/components/common/DocumentViewer';
import { useDocumentPreview } from '@/api/rpc/hooks/core/useDocuments';
import { getClassificationLabel } from '@/utils/emailClassificationUtils';

/** Minimal evidence-document shape needed to render the viewer. */
export interface EvidenceViewerDocument {
  id: string;
  originalFileName: string | null;
  classificationType: string | null;
}

interface Props {
  /** Ordered list of documents the viewer can page through. */
  documents: EvidenceViewerDocument[];
  /** LLM-generated readable names keyed by document id. */
  namesByDocumentId: Map<string, string>;
  /** Currently-open document id, or null when the viewer is closed. */
  openDocumentId: string | null;
  onClose: () => void;
  /** Switch the viewer to another document (prev/next navigation). */
  onNavigate: (documentId: string) => void;
}

/**
 * Fullscreen document previewer for collection-thread evidence. Reuses the
 * shared {@link DocumentViewer} for the content pane and lets the user page
 * across every document on the thread via edge chevrons or the arrow keys —
 * so reviewers no longer have to squint at a narrow slide-in or leave Gently
 * via "open in new tab".
 */
export function EvidenceViewerModal({
  documents,
  namesByDocumentId,
  openDocumentId,
  onClose,
  onNavigate,
}: Props) {
  const isOpen = !!openDocumentId;
  const currentIndex = documents.findIndex((d) => d.id === openDocumentId);
  const total = documents.length;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < total - 1;

  const goPrev = useCallback(() => {
    if (currentIndex > 0) onNavigate(documents[currentIndex - 1].id);
  }, [currentIndex, documents, onNavigate]);

  const goNext = useCallback(() => {
    if (currentIndex >= 0 && currentIndex < documents.length - 1) {
      onNavigate(documents[currentIndex + 1].id);
    }
  }, [currentIndex, documents, onNavigate]);

  // Arrow-key paging. Registered on the window while open (DOM is an external
  // system, so an effect is the right tool here). Ignore key events originating
  // from text inputs so typing in an embedded viewer isn't hijacked.
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goPrev, goNext]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        // Near-fullscreen shell — mirrors the codebase's established big-modal
        // recipe (AnnotationEditor). Reset the default padded/grid layout so the
        // viewer fills the whole surface.
        className="flex h-[92vh] max-h-[92vh] w-[96vw] max-w-[96vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[96vw]"
        aria-describedby={undefined}
      >
        {openDocumentId && currentIndex >= 0 ? (
          <EvidenceViewerBody
            doc={documents[currentIndex]}
            readableName={namesByDocumentId.get(openDocumentId)}
            position={currentIndex + 1}
            total={total}
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={goPrev}
            onNext={goNext}
          />
        ) : (
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle className="text-sm">
              <T context="evidence viewer title fallback">Document</T>
            </DialogTitle>
          </DialogHeader>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface BodyProps {
  doc: EvidenceViewerDocument;
  readableName: string | undefined;
  position: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

function EvidenceViewerBody({
  doc,
  readableName,
  position,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
}: BodyProps) {
  // Only needed for the "open in new tab" affordance; DocumentViewer fetches
  // its own copy (same query key, so this is served from cache).
  const { data } = useDocumentPreview(doc.id);

  const isEmailBody = isEmailBodyDocument(doc.originalFileName);
  const classificationLabel = doc.classificationType
    ? getClassificationLabel(doc.classificationType)
    : null;
  const title =
    readableName ?? classificationLabel ?? doc.originalFileName ?? null;
  const signedUrl = data?.signedUrl;

  return (
    <>
      {/* Header. Right padding leaves room for the Dialog's built-in close (X). */}
      <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-4 py-3 pr-12 text-left">
        <div className="min-w-0 flex-1">
          <DialogTitle className="truncate text-sm">
            {title ? (
              <Var>{title}</Var>
            ) : isEmailBody ? (
              <T context="evidence viewer title, docified email body">Email</T>
            ) : (
              <T context="evidence viewer title fallback">Document</T>
            )}
          </DialogTitle>
          <p className="truncate text-xs text-muted-foreground">
            <T context="evidence viewer position indicator, e.g. Document 2 of 5">
              Document <Var>{position}</Var> of <Var>{total}</Var>
            </T>
            {!isEmailBody && doc.originalFileName ? (
              <>
                {' · '}
                <Var>{doc.originalFileName}</Var>
              </>
            ) : null}
          </p>
        </div>
        {signedUrl && !isEmailBody && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 shrink-0"
            onClick={() =>
              window.open(signedUrl, '_blank', 'noopener,noreferrer')
            }
          >
            <ArrowSquareOutIcon className="h-3.5 w-3.5 mr-1" />
            <T context="document action, open in new tab">Open in new tab</T>
          </Button>
        )}
      </DialogHeader>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <DocumentViewer documentId={doc.id} className="h-full" />
        {total > 1 && (
          <>
            <NavArrow side="left" disabled={!hasPrev} onClick={onPrev} />
            <NavArrow side="right" disabled={!hasNext} onClick={onNext} />
          </>
        )}
      </div>
    </>
  );
}

function NavArrow({
  side,
  disabled,
  onClick,
}: {
  side: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
}) {
  const gt = useGT();
  return (
    <Button
      variant="outline"
      size="icon"
      disabled={disabled}
      onClick={onClick}
      aria-label={
        side === 'left'
          ? gt('Previous document', {
              context: 'aria-label, view previous evidence document',
            })
          : gt('Next document', {
              context: 'aria-label, view next evidence document',
            })
      }
      className={`absolute top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/90 shadow-md ${
        side === 'left' ? 'left-3' : 'right-3'
      }`}
    >
      {side === 'left' ? (
        <CaretLeftIcon className="h-5 w-5" />
      ) : (
        <CaretRightIcon className="h-5 w-5" />
      )}
    </Button>
  );
}
